import { useContext, useEffect, useCallback } from "react";
import { generateClient } from "@aws-amplify/api";
import { upsertSubject as _upsertSubject } from "@/graphql/mutations";
import { getSubjects as _getSubjects } from "@/graphql/queries";
import RootContext from "@/RootContext";

import {
  getAllergies as _getAllergies,
  getConditions as _getConditions,
  getMedications as _getMedications,
  getProviderTypes as _getProviderTypes,
  getRoutes as _getRoutes,
} from "@/graphql/queries";

const client = generateClient();

/* CONSTANTS */
const CUSTOM_ALLERGY = { id: null, name: "Custom Allergy" };
const CUSTOM_CONDITION = { id: null, name: "Custom Condition" };
const CUSTOM_MEDICATION = { id: null, name: "Custom Medication" };
const NULL_ROUTE = { id: null, name: "Unspecified" };

export default function useSubjectAPI(subjectId) {
  const rootContext = useContext(RootContext);

  // ---- Cache Keys (PER subject) ----
  const SUBJECT_KEY = subjectId ? `edit_getSubjects:${subjectId}` : "edit_getSubjects:null";

  const ALLERGIES_KEY = "edit_getAllergies";
  const CONDITIONS_KEY = "edit_getConditions";
  const MEDICATIONS_KEY = "edit_getMedications";
  const PROVIDER_TYPES_KEY = "edit_getProviderTypes";
  const ROUTES_KEY = "edit_getRoutes";

  // ---- Cache helpers ----
  const invalidateSubject = useCallback(() => {
    rootContext.set(SUBJECT_KEY, null);
  }, [rootContext, SUBJECT_KEY]);

  const fetchSubjectFromServer = useCallback(() => {
    if (!subjectId) return Promise.resolve(null);

    return client
      .graphql({
        query: _getSubjects,
        variables: {
          id: [Number(subjectId)],
          integrations: { clinicalConductor: true },
          subject_contacts_limit: 0,
        },
      })
      .then((res) => {
        const subject = res?.data?.getSubjects?.subjects?.[0] || null;
        return subject;
      });
  }, [subjectId]);

 
  const loadSubject = useCallback(
    async ({ force = false } = {}) => {
      try {
        if (!subjectId) return null;

        if (!force) {
          const cached = rootContext.get(SUBJECT_KEY);
          if (cached) return cached;
        }

        
        rootContext.set(SUBJECT_KEY, null);

        return rootContext.set(SUBJECT_KEY, fetchSubjectFromServer());
      } catch (err) {
        console.error("Load Subject Error:", err);
        return null;
      }
    },
    [subjectId, rootContext, SUBJECT_KEY, fetchSubjectFromServer]
  );


  const revalidateSubject = useCallback(async () => {
    return loadSubject({ force: true });
  }, [loadSubject]);

 
  useEffect(() => {
    return () => {
      rootContext.set(SUBJECT_KEY, null);
      
    };
  }, [rootContext, SUBJECT_KEY]);

  /* SAVE SUBJECT */
  const saveSubject = useCallback(
    async ({ input, input_delete, revalidate = true } = {}) => {
      try {
        const res = await client.graphql({
          query: _upsertSubject,
          variables: { input, input_delete },
        });

        
        invalidateSubject();

       
        if (revalidate) {
          await revalidateSubject();
        }

        return res?.data?.upsertSubject || null;
      } catch (err) {
        console.error("Save Subject Error:", err?.errors?.[0]?.message || err?.message);
        throw err;
      }
    },
    [invalidateSubject, revalidateSubject]
  );

 
  const saveSubjectDirect = useCallback(
    async (payload, {input_delete, revalidate = true } = {}) => {
      try {
        const cleanPayload = { ...payload };

        if (cleanPayload.subject_recruitment_sources) {
          cleanPayload.subject_recruitment_sources =
            cleanPayload.subject_recruitment_sources.filter((e) => e.recruitment_id);
          if (cleanPayload.subject_recruitment_sources.length === 0) {
            delete cleanPayload.subject_recruitment_sources;
          }
        }

        if (cleanPayload.subject_deal_status) {
          cleanPayload.subject_deal_status =
            cleanPayload.subject_deal_status.filter((d) => d.deal_id);
          if (cleanPayload.subject_deal_status.length === 0) {
            delete cleanPayload.subject_deal_status;
          }
        }

        if (cleanPayload.subject_enrollment_status) {
          cleanPayload.subject_enrollment_status =
            cleanPayload.subject_enrollment_status.filter((e) => e.study_id);
          if (cleanPayload.subject_enrollment_status.length === 0) {
            delete cleanPayload.subject_enrollment_status;
          }
        }

        const res = await client.graphql({
          query: _upsertSubject,
          variables: { input: cleanPayload,
            input_delete: input_delete
           },
        });

        invalidateSubject();

        if (revalidate) {
          await revalidateSubject();
        }

        return res?.data?.upsertSubject || null;
      } catch (err) {
        console.error("Save Subject Direct Error:", err?.errors?.[0]?.message || err?.message);
        return null;
      }
    },
    [invalidateSubject, revalidateSubject]
  );

  /* LOOKUPS (cached globally) */
  const getAllergies = useCallback(async () => {
    return (
      rootContext.get(ALLERGIES_KEY) ||
      rootContext.set(
        ALLERGIES_KEY,
        client
          .graphql({
            query: _getAllergies,
            variables: { sorting: { field: "name", order: "asc" } },
          })
          .then((res) => [CUSTOM_ALLERGY, ...(res?.data?.getAllergies?.allergies || [])])
      )
    );
  }, [rootContext]);

  const getConditions = useCallback(async () => {
    return (
      rootContext.get(CONDITIONS_KEY) ||
      rootContext.set(
        CONDITIONS_KEY,
        client
          .graphql({
            query: _getConditions,
            variables: { sorting: { field: "name", order: "asc" } },
          })
          .then((res) => [CUSTOM_CONDITION, ...(res?.data?.getConditions?.conditions || [])])
      )
    );
  }, [rootContext]);

  const getMedications = useCallback(async () => {
    return (
      rootContext.get(MEDICATIONS_KEY) ||
      rootContext.set(
        MEDICATIONS_KEY,
        client
          .graphql({
            query: _getMedications,
            variables: { sorting: { field: "name", order: "asc" } },
          })
          .then((res) => [...(res?.data?.getMedications?.medications || []), CUSTOM_MEDICATION])
      )
    );
  }, [rootContext]);

  const getProviderTypes = useCallback(async () => {
    return (
      rootContext.get(PROVIDER_TYPES_KEY) ||
      rootContext.set(
        PROVIDER_TYPES_KEY,
        client
          .graphql({
            query: _getProviderTypes,
            variables: { sorting: { field: "type", order: "asc" } },
          })
          .then((res) => res?.data?.getProviderTypes?.provider_types || [])
      )
    );
  }, [rootContext]);

  const getRoutes = useCallback(async () => {
    return (
      rootContext.get(ROUTES_KEY) ||
      rootContext.set(
        ROUTES_KEY,
        client
          .graphql({
            query: _getRoutes,
            variables: { sorting: { field: "name", order: "asc" } },
          })
          .then((res) => [NULL_ROUTE, ...(res?.data?.getRoutes?.routes || [])])
      )
    );
  }, [rootContext]);

  return {
    // subject
    loadSubject,
    revalidateSubject,
    invalidateSubject,

    // save
    saveSubject,
    saveSubjectDirect,

    // lookups
    getAllergies,
    getConditions,
    getMedications,
    getProviderTypes,
    getRoutes,

    // constants
    CUSTOM_ALLERGY,
    CUSTOM_CONDITION,
    CUSTOM_MEDICATION,
    NULL_ROUTE,
  };
}